/**
 * Someone else's Mintly invite code, held while the visitor finds their way to signing up.
 *
 * A visitor lands on /invite/<code>, the code is kept here, and the sign-up sends it —
 * with the form for email, straight afterwards for Google and Facebook. Whoever's code
 * it is then earns a share of Mintly's fee on what the new user makes.
 *
 * Kept apart from utils/referral, which holds affiliate codes: those point at one
 * product and are spent on joining it, these are spent once, on signing up.
 *
 * @see mintly-back/src/services/referralService
 */

const KEY = "mintly_invite";
const WINDOW_DAYS = 30;

/** Remember a code. A newer link replaces an older one. */
export function storeInvite(code) {
  const clean = String(code || "").trim().toUpperCase();
  if (typeof window === "undefined" || !clean) return;
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({ code: clean, expiresAt: Date.now() + WINDOW_DAYS * 24 * 60 * 60 * 1000 })
    );
  } catch {
    /* storage disabled — the visitor signs up uninvited */
  }
}

/** The code waiting to be used, or "" when there is none or it has expired. */
export function getStoredInvite() {
  if (typeof window === "undefined") return "";
  try {
    const entry = JSON.parse(window.localStorage.getItem(KEY) || "null");
    if (!entry?.code) return "";
    if (entry.expiresAt && entry.expiresAt < Date.now()) {
      window.localStorage.removeItem(KEY);
      return "";
    }
    return entry.code;
  } catch {
    return "";
  }
}

/** Forget it — once used, it can't attach anyone else from this browser by mistake. */
export function clearInvite() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* nothing to clean up */
  }
}
